import React, { ReactNode, useCallback, useState } from 'react';

type TabItem = {
    id: string;
    label: string;
    content: ReactNode;
};

type TabsPanelProps = {
    tabs: TabItem[];
    selectedId?: string;
    onTabChange?: (id: string) => void;
    topRight?: ReactNode;
};

const TabsPanel = ({ tabs, selectedId, onTabChange, topRight }: TabsPanelProps): JSX.Element => {
    const [internalTab, setInternalTab] = useState<string>(tabs[0]?.id ?? '');
    const activeId = selectedId ?? internalTab;

    const handleTabClick = useCallback(
        (tab: TabItem): void => {
            if (onTabChange) {
                onTabChange(tab.id);
            } else {
                setInternalTab(tab.id);
            }
        },
        [onTabChange],
    );

    const activeTab = tabs.find(t => t.id === activeId) ?? tabs[0];

    return (
        <div className="component-tabs-panel">
            <div className="tab-bar">
                <ul role="tablist">
                    {tabs.map(tab => (
                        <li
                            className={`tab ${activeId === tab.id ? 'has-focus' : ''}`}
                            key={tab.id}
                            role="tab"
                            tabIndex={0}
                            aria-selected={activeId === tab.id}
                            onClick={() => handleTabClick(tab)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleTabClick(tab);
                                }
                            }}
                        >
                            <p>{tab.label}</p>
                        </li>
                    ))}
                </ul>
                {topRight ? <div className="tab-bar-right">{topRight}</div> : null}
            </div>
            <div
                className="tab-content"
                role="tabpanel"
            >
                {activeTab?.content}
            </div>
        </div>
    );
};

export default TabsPanel;
